import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen,
  Check,
  FileText,
  HelpCircle,
  Lightbulb,
  Loader2,
  MessageCircle,
  Mic,
  Play,
  Send,
  Settings,
  Sparkles,
  Square,
  Volume2,
  X,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import CreditUpsellModal, { FALLBACK_MODE_COSTS, getActiveExamSessionId, useTutorCredits } from './CreditUpsellModal';
import { LinceMascot } from './TuPlazaIALogo';
import { API } from '../config/api';
import apiClient from '../services/api';

const TUTOR_TOPICS = [
  { id: 'explain', label: 'Explícame un tema', icon: BookOpen, prompt: 'Explícame de forma clara y didáctica el tema: ' },
  { id: 'doubt', label: 'Tengo una duda', icon: HelpCircle, prompt: 'Tengo una duda sobre: ' },
  { id: 'summary', label: 'Resumen rápido', icon: FileText, prompt: 'Dame un resumen conciso de: ' },
  { id: 'tips', label: 'Técnicas estudio', icon: Lightbulb, prompt: 'Dame técnicas de memorización para: ' },
];

// El coste por modo NO vive aquí: lo sirve GET /tutor/credits-info (modes) y es
// exactamente lo que cobra el backend. `voice` marca los modos que exigen plan con voz.
const INTERACTION_MODES = [
  { id: 'text_only', label: 'Solo texto', icon: '⌨️', desc: 'Escribe y lee', voice: false },
  { id: 'text_with_audio', label: 'Texto + Audio', icon: '🔊', desc: 'Escribe y escucha', voice: true },
  { id: 'voice_with_text', label: 'Voz → Texto', icon: '🎤', desc: 'Habla y lee', voice: true },
  { id: 'voice_only', label: 'Solo voz', icon: '🎙️', desc: 'Habla y escucha', voice: true },
];

// Rótulo bajo cada respuesta: "Material consultado", nunca "Basado en" — el
// modelo puede equivocarse aunque haya material (Tong 2024).
const formatSources = (sources) =>
  (sources || [])
    .map((s) => (typeof s === 'string' ? s : s?.titulo))
    .filter(Boolean)
    .join(' · ');

const TutorChat = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [showModeSelector, setShowModeSelector] = useState(true);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [interactionMode, setInteractionMode] = useState('text_only');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [_creditsUsed, setCreditsUsed] = useState(0);

  // Única fuente de saldo, precios y acceso: GET /tutor/credits-info
  const { credits: aiCredits, loading: checkingAccess, refetch: refetchCredits, setCredits } = useTutorCredits();
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [upsellData, setUpsellData] = useState({ required: 0, message: '' });

  // Pages where TutorChat should NOT appear (public/landing pages)
  const excludedPaths = useMemo(() => ['/', '/auth', '/pricing', '/aviso-legal', '/privacidad', '/contacto', '/logos', '/auth/callback', '/primer-dia'], []);

  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [_audioChunks, setAudioChunks] = useState([]);

  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const recordingTimerRef = useRef(null);

  // Con el cupo agotado el widget sigue visible: el aviso se muestra dentro del chat
  const hasTutorAccess = Boolean(aiCredits?.has_access) || aiCredits?.reason === 'tope_diario';
  const voiceAllowed = Boolean(aiCredits?.voice_allowed);
  const remainingToday = aiCredits?.remaining_today ?? null;
  const dailyCap = aiCredits?.daily_cap ?? null;

  // Re-check access/credits when the route changes (after login redirect) or on login/logout
  useEffect(() => {
    refetchCredits();
    const handleStorageChange = () => refetchCredits();
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [location.pathname, refetchCredits]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Cleanup recording timer
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  const availableModes = useMemo(
    () => INTERACTION_MODES.filter((m) => !m.voice || voiceAllowed),
    [voiceAllowed],
  );

  // Si el plan no incluye voz y el modo actual la exige, volvemos a texto
  useEffect(() => {
    if (aiCredits && !voiceAllowed && interactionMode !== 'text_only') {
      setInteractionMode('text_only');
    }
  }, [aiCredits, voiceAllowed, interactionMode]);

  const costFor = useCallback(
    (modeId) => {
      const fromApi = aiCredits?.costs?.[modeId];
      if (typeof fromApi === 'number') return fromApi;
      return FALLBACK_MODE_COSTS[modeId] ?? FALLBACK_MODE_COSTS.text_only;
    },
    [aiCredits],
  );

  const costLabel = useCallback(
    (modeId) => {
      const cost = costFor(modeId);
      if (cost === 0) return 'Gratis en tu prueba';
      return `${cost} créd/msg`;
    },
    [costFor],
  );

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });

      const chunks = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await handleVoiceInput(blob);
      };

      setAudioChunks([]);
      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);

      toast.info('🎤 Grabando... Pulsa de nuevo para enviar');
    } catch (_error) {
      toast.error('No se pudo acceder al micrófono. Revisa los permisos.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const playAudio = useCallback((url, messageIndex) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }

    // Handle both relative URLs and blob URLs
    const audioUrl = url.startsWith('blob:') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    audio.onplay = () => setPlayingAudio(messageIndex);
    audio.onended = () => setPlayingAudio(null);
    audio.onerror = () => {
      setPlayingAudio(null);
      toast.error('Error al reproducir audio');
    };

    audio.play();
  }, []);

  // Respuesta del backend → mensaje del asistente (con fuentes y cobro real)
  const assistantMessageFrom = useCallback((data, extra = {}) => ({
    role: 'assistant',
    content: data.response,
    audio_url: data.audio_url,
    credits_used: data.credits_charged ?? data.credits_used ?? 0,
    sources: data.sources || [],
    grounded: Boolean(data.grounded),
    hints_mode: Boolean(data.hints_mode),
    topic_name: data.topic_name || null,
    timestamp: new Date(),
    ...extra,
  }), []);

  // 402 / 403 / 429 con acción: modal, aviso honesto o cupo agotado
  const handleTutorError = useCallback((error, { removeLastUser = false } = {}) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    const detailText = typeof detail === 'string' ? detail : (detail?.message || null);
    if (removeLastUser) {
      setMessages(prev => (prev.length && prev[prev.length - 1].role === 'user' ? prev.slice(0, -1) : prev));
    }
    if (status === 402) {
      setUpsellData({
        required: detail?.required || costFor(interactionMode),
        message: detailText || 'Créditos insuficientes',
      });
      setShowUpsellModal(true);
      return;
    }
    if (status === 429) {
      setCredits(prev => (prev ? { ...prev, remaining_today: 0 } : prev));
      toast.info(detailText || 'Has usado los mensajes de hoy del Tutor. Mañana vuelves a tenerlos.');
      return;
    }
    if (status === 403) {
      toast.info(detailText || 'Tu plan no incluye esta función del Tutor.');
      refetchCredits();
      return;
    }
    toast.error(detailText || 'Error al conectar con el tutor');
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: 'Lo siento, ha ocurrido un error. Por favor, intenta de nuevo.',
      isError: true,
      timestamp: new Date()
    }]);
  }, [costFor, interactionMode, refetchCredits, setCredits]);

  const handleVoiceInput = useCallback(async (audioBlob) => {
    if (!audioBlob || audioBlob.size === 0) {
      toast.error('No se grabó audio. Intenta de nuevo.');
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      // Determine if we want audio response based on mode
      const wantAudioResponse = interactionMode === 'voice_only' || interactionMode === 'text_with_audio';

      if (interactionMode === 'voice_only' || interactionMode === 'voice_with_text') {
        // Use voice-to-voice endpoint
        formData.append('include_audio_response', wantAudioResponse.toString());

        const response = await apiClient.post(
          `${API}/tutor/voice-chat`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        // Add user message (transcribed)
        const userMessage = {
          role: 'user',
          content: response.data.user_text,
          isVoice: true,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, userMessage]);

        // Add AI response
        const aiMessage = assistantMessageFrom(response.data);
        setMessages(prev => [...prev, aiMessage]);
        setCreditsUsed(prev => prev + (aiMessage.credits_used || 0));
        refetchCredits();

        // Auto-play audio if available
        if (response.data.audio_url) {
          playAudio(response.data.audio_url, messages.length + 1);
        }

        toast.success('✅ Respuesta recibida');
      } else {
        // Just transcribe for text modes
        const response = await apiClient.post(
          `${API}/tutor/transcribe`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (!(response.data.text || '').trim()) {
          toast.info('No te he oído bien. Repítelo, por favor. No se te ha cobrado.');
          return;
        }
        // Set the transcribed text as input
        setInput(response.data.text);
        toast.success('🎤 Transcripción completada. Pulsa enviar o edita el texto.');
      }
    } catch (error) {
      handleTutorError(error);
    } finally {
      setLoading(false);
    }
  }, [interactionMode, messages, playAudio, assistantMessageFrom, refetchCredits, handleTutorError]);

  const handleSend = useCallback(async (customPrompt = null) => {
    const messageText = customPrompt || input;
    if (!messageText.trim()) return;

    const creditCost = costFor(interactionMode);

    // Cupo diario de la prueba agotado: no enviamos nada
    if (aiCredits && aiCredits.daily_cap && aiCredits.remaining_today === 0) {
      toast.info(`Has usado los ${aiCredits.daily_cap} mensajes de hoy del Tutor. Mañana vuelves a tener ${aiCredits.daily_cap}.`);
      return;
    }

    // Saldo insuficiente (misma cifra que el header): modal con salida, sin llamar al backend
    if (aiCredits && !aiCredits.is_unlimited && creditCost > 0 && aiCredits.balance < creditCost) {
      setUpsellData({
        required: creditCost,
        message: `Este mensaje cuesta ${creditCost} créditos y tienes ${aiCredits.balance}.`
      });
      setShowUpsellModal(true);
      return;
    }

    const userMessage = { role: 'user', content: messageText, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    const wantAudioResponse = interactionMode === 'text_with_audio' || interactionMode === 'voice_only';

    try {
      // Un solo cobro por mensaje: lo hace /tutor/chat (nada de pre-cobros en otro saldo)
      const response = await apiClient.post(
        `${API}/tutor/chat`,
        {
          message: messageText,
          include_audio: wantAudioResponse,
          // Si hay un test en curso, el backend entra en modo pistas (y lo detecta él mismo
          // para simulacros/oficiales aunque este dato no llegue)
          context: { exam_session_id: getActiveExamSessionId() || undefined },
        }
      );

      const aiMessage = assistantMessageFrom(response.data);
      setMessages(prev => [...prev, aiMessage]);
      setCreditsUsed(prev => prev + (aiMessage.credits_used || 0));

      // El saldo y el cupo restante vienen del backend: misma cifra que el header
      if (typeof response.data.remaining_today === 'number') {
        setCredits(prev => (prev ? { ...prev, remaining_today: response.data.remaining_today } : prev));
      }
      refetchCredits();

      // Auto-play audio if enabled and available
      if (wantAudioResponse && response.data.audio_url) {
        playAudio(response.data.audio_url, messages.length);
      } else if (wantAudioResponse && !response.data.audio_url) {
        toast.info('Audio no disponible: te dejo la respuesta por escrito y no se te ha cobrado el audio.');
      }
    } catch (error) {
      handleTutorError(error, { removeLastUser: true });
    } finally {
      setLoading(false);
    }
  }, [input, interactionMode, aiCredits, messages, refetchCredits, setCredits, playAudio, costFor, assistantMessageFrom, handleTutorError]);

  const generateAudio = useCallback(async (text, messageIndex) => {
    setGeneratingAudio(true);
    try {
      const response = await apiClient.post(
        `${API}/tutor/generate-audio`,
        { text: text.substring(0, 1000) },
        {
          responseType: 'blob'
        }
      );

      const audioUrl = URL.createObjectURL(response.data);

      setMessages(prev => prev.map((msg, idx) =>
        idx === messageIndex ? { ...msg, audio_url: audioUrl } : msg
      ));

      playAudio(audioUrl, messageIndex);
      refetchCredits();
      toast.success('Audio generado');
    } catch (error) {
      handleTutorError(error);
    } finally {
      setGeneratingAudio(false);
    }
  }, [playAudio, refetchCredits, handleTutorError]);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingAudio(null);
  }, []);

  const handleTopicClick = useCallback((topic) => {
    setInput(topic.prompt);
  }, []);

  const selectMode = (modeId) => {
    setInteractionMode(modeId);
    setShowModeSelector(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentMode = useMemo(() => INTERACTION_MODES.find(m => m.id === interactionMode), [interactionMode]);
  const isVoiceInput = useMemo(() => interactionMode === 'voice_only' || interactionMode === 'voice_with_text', [interactionMode]);

  // Don't render anything if still checking access, on excluded paths, or no tutor access
  const shouldHide = useMemo(
    () => checkingAccess || excludedPaths.includes(location.pathname) || !hasTutorAccess,
    [checkingAccess, excludedPaths, location.pathname, hasTutorAccess],
  );

  const balanceLabel = aiCredits?.is_unlimited ? '∞' : (aiCredits?.balance ?? 0);
  const quotaLabel = dailyCap
    ? `Hoy: ${remainingToday ?? dailyCap}/${dailyCap} mensajes`
    : null;
  const showLowBalance = Boolean(
    aiCredits && !aiCredits.is_unlimited && (aiCredits.message_cost || 0) > 0 && aiCredits.balance < 20,
  );

  const renderModeButton = (mode, compact = false) => (
    <button
      key={mode.id}
      onClick={() => {
        selectMode(mode.id);
        if (compact) setShowSettingsModal(false);
      }}
      aria-label={`${mode.label}: ${mode.desc} - ${costLabel(mode.id)}`}
      aria-pressed={interactionMode === mode.id}
      className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${interactionMode === mode.id
        ? 'border-indigo-500 bg-indigo-50'
        : 'border-slate-200 bg-white hover:border-indigo-300'
        }`}
    >
      <span className={compact ? 'text-2xl' : 'text-3xl'}>{mode.icon}</span>
      <div className="text-left flex-1">
        <p className="font-semibold text-slate-800">{mode.label}</p>
        <p className="text-xs text-slate-500">{mode.desc}</p>
      </div>
      <div className="text-right">
        <span className="text-sm font-bold text-indigo-600">
          {costFor(mode.id) === 0 ? 'Gratis' : costFor(mode.id)}
        </span>
        <p className="text-xs text-slate-400">{costFor(mode.id) === 0 ? 'en tu prueba' : 'créd/msg'}</p>
      </div>
    </button>
  );

  return (
    <div style={shouldHide ? { display: 'none' } : undefined}>
      {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-24 right-6 w-[420px] h-[600px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col z-50"
            data-testid="tutor-chat-modal"
          >
            {/* Mode Selector Screen */}
            {showModeSelector && messages.length === 0 ? (
              <div className="flex flex-col h-full">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-t-2xl">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <Sparkles className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="font-bold block">Tutor IA TuPlazaIA</span>
                        <span className="text-xs opacity-80">Elige cómo quieres comunicarte</span>
                      </div>
                    </div>
                    <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 rounded-full p-2" aria-label="Cerrar tutor">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="flex-1 p-6 bg-slate-50 flex flex-col">
                  <div className="text-center mb-6">
                    <LinceMascot size={80} mood="happy" className="mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-slate-800">¿Cómo prefieres comunicarte?</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      {voiceAllowed
                        ? 'Selecciona tu modo de interacción'
                        : 'En tu plan el tutor responde por escrito; la voz y el avatar están en el Plan Top'}
                    </p>
                  </div>

                  <div className="space-y-3 flex-1">
                    {availableModes.map((mode) => renderModeButton(mode))}
                  </div>

                  <Button
                    onClick={() => setShowModeSelector(false)}
                    className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-3"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Comenzar con {currentMode?.label}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-t-2xl">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <Sparkles className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="font-bold block">Tutor IA TuPlazaIA</span>
                        <span className="text-xs opacity-80">{currentMode?.icon} {currentMode?.label}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowSettingsModal(true)}
                        className="p-2 rounded-full transition-colors bg-white/20 hover:bg-white/30"
                        title="Cambiar modo"
                        aria-label="Cambiar modo de interacción"
                        data-testid="tutor-settings-btn"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 rounded-full p-2" aria-label="Cerrar tutor" data-testid="close-tutor-btn">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Credits indicator: la misma cifra que el header del Dashboard */}
                  <div className="mt-3 text-xs bg-white/10 rounded-lg px-3 py-1.5 flex items-center justify-between" data-testid="tutor-credits-indicator">
                    <span className="flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      Saldo: {balanceLabel} créditos
                    </span>
                    <span className="opacity-80" data-testid="tutor-mode-cost">
                      {quotaLabel || costLabel(interactionMode)}
                    </span>
                  </div>

                  {/* Low balance: informar sin alarmar, con salida */}
                  {showLowBalance && (
                    <div className="mt-2 text-xs px-3 py-1.5 rounded-lg flex items-center gap-2 bg-white/10 text-indigo-50">
                      <Zap className="w-3 h-3" />
                      Te quedan {aiCredits.balance} créditos para el Tutor
                      <button
                        onClick={() => setShowUpsellModal(true)}
                        className="ml-auto underline hover:no-underline"
                      >
                        Ver opciones
                      </button>
                    </div>
                  )}
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50" role="log" aria-live="polite" aria-label="Mensajes del tutor">
                  {messages.length === 0 && (
                    <div className="text-center py-4">
                      <LinceMascot size={60} mood="happy" className="mx-auto mb-4" />
                      <p className="font-bold text-slate-800 mb-2">¡Hola! Soy tu Tutor IA</p>
                      <p className="text-sm text-slate-600 mb-4">
                        {isVoiceInput
                          ? 'Pulsa el micrófono y háblame tu duda'
                          : 'Escribe tu pregunta o elige un tema'}
                      </p>

                      {/* Quick topics */}
                      <div className="grid grid-cols-2 gap-2 text-left">
                        {TUTOR_TOPICS.map((topic) => {
                          const TopicIcon = topic.icon;
                          return (
                            <button
                              key={topic.id}
                              onClick={() => handleTopicClick(topic)}
                              className="flex items-center gap-2 p-3 bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all text-sm"
                            >
                              <TopicIcon className="w-4 h-4 text-indigo-600" />
                              <span className="text-slate-700">{topic.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {messages.map((msg, index) => (
                    <motion.div
                      key={msg.id || `msg-${index}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      data-testid={`chat-message-${index}`}
                    >
                      <div className={`max-w-[85%]`}>
                        <div
                          className={`p-3 rounded-2xl ${msg.role === 'user'
                            ? 'bg-indigo-600 text-white rounded-br-sm'
                            : msg.isError
                              ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-sm'
                              : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
                            }`}
                        >
                          {msg.isVoice && (
                            <div className="flex items-center gap-1 text-xs opacity-70 mb-1">
                              <Mic className="w-3 h-3" /> Mensaje de voz
                            </div>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          {msg.role === 'assistant' && !msg.isError && msg.sources !== undefined && (
                            msg.grounded && msg.sources.length > 0 ? (
                              <p className="mt-2 text-[11px] text-slate-500" data-testid="tutor-sources">
                                Material consultado: {formatSources(msg.sources)}
                              </p>
                            ) : (
                              <p className="mt-2 text-[11px] text-amber-700" data-testid="tutor-no-sources">
                                Sin material del temario detectado: respuesta general, contrástala antes de memorizar.
                              </p>
                            )
                          )}
                          {msg.hints_mode && (
                            <p className="mt-1 text-[11px] text-indigo-600">
                              Modo pistas: durante un test el tutor no revela la respuesta.
                            </p>
                          )}
                        </div>

                        {/* Audio controls for AI messages */}
                        {msg.role === 'assistant' && !msg.isError && (
                          <div className="mt-2 flex items-center gap-2">
                            {msg.audio_url ? (
                              <button
                                onClick={() => playingAudio === index ? stopAudio() : playAudio(msg.audio_url, index)}
                                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${playingAudio === index
                                  ? 'bg-indigo-600 text-white'
                                  : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                                  }`}
                              >
                                {playingAudio === index ? (
                                  <>
                                    <Square className="w-3 h-3" />
                                    Detener
                                  </>
                                ) : (
                                  <>
                                    <Play className="w-3 h-3" />
                                    Escuchar
                                  </>
                                )}
                              </button>
                            ) : voiceAllowed ? (
                              <button
                                onClick={() => generateAudio(msg.content, index)}
                                disabled={generatingAudio}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors disabled:opacity-50"
                              >
                                {generatingAudio ? (
                                  <>
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Generando...
                                  </>
                                ) : (
                                  <>
                                    <Volume2 className="w-3 h-3" />
                                    Generar audio
                                  </>
                                )}
                              </button>
                            ) : null}

                            <span className="text-xs text-slate-400">
                              {msg.credits_used > 0
                                ? `${msg.credits_used} crédito${msg.credits_used > 1 ? 's' : ''}`
                                : (msg.credits_used === 0 ? 'Sin coste' : '')}
                            </span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}

                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm p-3">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                          <span className="text-sm text-slate-500">
                            {isRecording ? 'Procesando audio...' : 'El tutor está pensando...'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-4 border-t border-slate-200 bg-white rounded-b-2xl">
                  {dailyCap && remainingToday === 0 && (
                    <p className="text-xs text-slate-500 mb-2" data-testid="tutor-quota-exhausted">
                      Has usado los {dailyCap} mensajes de hoy del Tutor. Mañana vuelves a tener {dailyCap};
                      mientras tanto, sigue con tests, repaso de fallos y flashcards.
                    </p>
                  )}
                  {isVoiceInput ? (
                    /* Voice Input Mode */
                    <div className="flex flex-col items-center gap-3">
                      <button
                        onClick={isRecording ? stopRecording : startRecording}
                        disabled={loading}
                        aria-label={isRecording ? 'Detener grabación y enviar' : 'Iniciar grabación de voz'}
                        className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${isRecording
                          ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                          : 'bg-indigo-600 hover:bg-indigo-700'
                          } text-white disabled:opacity-50`}
                      >
                        {isRecording ? (
                          <Square className="w-6 h-6" />
                        ) : (
                          <Mic className="w-6 h-6" />
                        )}
                      </button>
                      <p className="text-sm text-slate-500">
                        {isRecording
                          ? `🔴 Grabando ${formatTime(recordingTime)}... Pulsa para enviar`
                          : 'Pulsa para hablar'
                        }
                      </p>
                      {/* Also allow text input */}
                      <div className="w-full flex gap-2 mt-2">
                        <input
                          type="text"
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && !loading && handleSend()}
                          placeholder="O escribe aquí..."
                          className="flex-1 px-4 py-2 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none text-sm"
                          disabled={loading || isRecording}
                        />
                        {input && (
                          <Button
                            onClick={() => handleSend()}
                            disabled={loading || !input.trim()}
                            className="px-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* Text Input Mode */
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && !loading && handleSend()}
                        placeholder="Pregunta al tutor..."
                        aria-label="Escribe tu pregunta al tutor"
                        className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none text-sm"
                        disabled={loading || (Boolean(dailyCap) && remainingToday === 0)}
                        data-testid="tutor-input"
                      />
                      <Button
                        onClick={() => handleSend()}
                        disabled={loading || !input.trim() || (Boolean(dailyCap) && remainingToday === 0)}
                        className="px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
                        aria-label="Enviar mensaje"
                        data-testid="tutor-send-btn"
                      >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </motion.div>
      )}

      {/* Floating Button */}
      {!shouldHide && <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full shadow-lg flex items-center justify-center z-50"
        aria-label={isOpen ? 'Cerrar tutor IA' : 'Abrir tutor IA'}
        data-testid="tutor-toggle-btn"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </motion.button>}

      {/* Credit Upsell Modal (honesto: solo vende si hay pagos; si no, salidas de estudio) */}
      <CreditUpsellModal
        isOpen={showUpsellModal}
        onClose={() => setShowUpsellModal(false)}
        currentBalance={aiCredits?.balance || 0}
        requiredCredits={upsellData.required}
        message={upsellData.message}
      />

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" role="dialog" aria-modal="true" aria-labelledby="tutor-settings-title" onClick={() => setShowSettingsModal(false)} onKeyDown={(e) => { if (e.key === 'Escape') setShowSettingsModal(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl" role="presentation" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-t-2xl flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5" />
                <span id="tutor-settings-title" className="font-bold">Configuración del Tutor</span>
              </div>
              <button onClick={() => setShowSettingsModal(false)} className="hover:bg-white/20 rounded-full p-1" aria-label="Cerrar configuración">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <h3 className="font-semibold text-slate-800 mb-4">Modo de interacción</h3>
              <div className="space-y-3">
                {availableModes.map((mode) => renderModeButton(mode, true))}
              </div>
              {!voiceAllowed && (
                <p className="text-xs text-slate-500 mt-3">Voz y avatar: disponibles en el Plan Top.</p>
              )}

              <div className="mt-6 pt-4 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Créditos disponibles:</span>
                  <span className="font-bold text-indigo-600">
                    {aiCredits?.is_unlimited ? '∞ Ilimitados' : `${aiCredits?.balance || 0} créditos`}
                  </span>
                </div>
                {quotaLabel && (
                  <div className="flex items-center justify-between text-sm mt-2">
                    <span className="text-slate-600">Mensajes de la prueba:</span>
                    <span className="font-bold text-indigo-600">{quotaLabel}</span>
                  </div>
                )}
                {showLowBalance && (
                  <Button
                    onClick={() => { setShowSettingsModal(false); setShowUpsellModal(true); }}
                    className="w-full mt-3 bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Ver opciones de créditos
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(TutorChat);
